import {FC, useState, useId, useEffect, useCallback} from 'react';
import {GridColDef, GridFilterModel, GridSortModel} from '@mui/x-data-grid-pro';
import {Box, Typography} from '@mui/material';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import './ManageIndicesDetailGrid.scss';
import {ReactComponent as ResetIcon} from '../../../../../assets/images/icon-reset.svg';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import FilterTextField from '../../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {Trans, useTranslation} from 'react-i18next';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {
  IAIInvestigateIndicesDetailsPayload,
  IAIInvestigateIndicesDetailsResponse,
  IAIInvestigateIndexDetails,
  IIndexDetailsDataUpdatedNotification,
  IRetryInvestigateIndexResponse,
  ManageIndicesStatus,
} from '../../../../../api/types/aiInvestigate.type';
import {
  useGetIndicesDetails,
  useGetAIInvestigateIndicesDetailMasterDocListId,
  useRetryInvestigateIndex,
} from '../../../../../hooks/ai/investigate/useInvestigate';
import {useDispatch, useSelector} from 'react-redux';
import {
  BgNavigationTypes,
  SourceTypeCode,
  ToastType,
} from '../../../../../shared/constants';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../../redux/slice/docListSlice';
import {RootState} from '../../../../../redux/store';
import {useLocation} from 'react-router-dom';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {IndicesDetailDocType} from '../../AiInvestigateUtils';
import {formatLastModifiedUtc} from '../../../../../utils/groupTranscriptsByDate';
import CellWrapper from '../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

type ManageIndicesDetailGridProps = {
  indexId: string;
  triggerRefetch?: boolean;
  hasAnyIndexPending?: (isProcessing: boolean) => void;
  documentCountChange?: (count: number) => void;
};

type ManageIndicesDetailRow = {
  id: number;
  dateSubmitted: string;
  source: string;
  updatedBy: string;
  action: string;
  includedDocs: number;
  excludedDocs: number;
  lastModifiedDate: string;
  status: string;
};

const INDEX_DETAILS_NOTIFICATION_TYPE = 'IndexDetailsDataUpdatedNotification';

const ManageIndicesDetailGrid: FC<ManageIndicesDetailGridProps> = ({
  indexId,
  triggerRefetch = false,
  hasAnyIndexPending,
  documentCountChange,
}) => {
  const dispatch = useDispatch();

  const [filterModel, setFilterModel] = useState<GridFilterModel>({items: []});
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);

  const [rows, setRows] = useState<ManageIndicesDetailRow[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const docListMasterId = useSelector(
    (state: RootState) => state.docList.docListId
  );

  const location = useLocation();
  const row = location.state;
  const [isNavigating, setIsNavigating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);

  const {t} = useTranslation();
  const titleId = useId();
  const descId = useId();
  const isActiveIndex = row?.active ?? false;
  const {mutate: getIndicesDetailMasterDocListId} =
    useGetAIInvestigateIndicesDetailMasterDocListId((response) => {
      dispatch(setDocListId(Number(response.doclistMasterId)));
    });
  const {mutate: retryInvestigateIndexMutation} = useRetryInvestigateIndex();

  const handleDocsClick = useCallback(
    (row: any, type: IndicesDetailDocType) => {
      getIndicesDetailMasterDocListId(
        {
          indexdetailId: row.id,
          type: type,
        },
        {
          onSuccess: (res) => {
            setIsNavigating(true);
            setIsLoading(true);
          },
        }
      );
    },
    [getIndicesDetailMasterDocListId]
  );

  const toastHandler = useCallback((message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  }, []);

  const retryHandler = (id: string) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id.toString() == id
          ? {...row, status: ManageIndicesStatus.Retrying}
          : row
      )
    );

    retryInvestigateIndexMutation(
      {indexId, indexDetailsId: id},
      {
        onSuccess: (data: IRetryInvestigateIndexResponse) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id.toString() == id
                ? {
                    ...row,
                    updatedBy: data.updatedBy,
                    lastModifiedDate: formatLastModifiedUtc(
                      data.lastModifiedDate
                    ),
                    status: data.status,
                  }
                : row
            )
          );

          hasAnyIndexPending?.(true);
          toastHandler(
            t(
              'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.retrySuccessToastMessage'
            ),
            ToastType.Success
          );
        },
        onError: () => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id.toString() == id
                ? {...row, status: ManageIndicesStatus.Failed}
                : row
            )
          );
          toastHandler(
            t(
              'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.retryErrorToastMessage'
            ),
            ToastType.Error
          );
        },
      }
    );
  };

  // Check the signalR message navigate accordingly
  useEffect(() => {
    if (!isNavigating) return;
    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListMasterId) &&
        navType === SourceTypeCode.DOCLIST
      ) {
        setIsLoading(false);
        setIsNavigating(false);
        const backPath = `${window.location.pathname}${window.location.search}`;
        dispatch(
          setDocListBackNavigation({
            label: t(
              'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.indexDetails'
            ),
            path: backPath,
            navState: row,
          })
        );
        const newWindow = window.open('about:blank', '_blank');
        if (newWindow) {
          newWindow.location.href = `/Document/DocList`;
        }
      }
      return;
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      setIsLoading(false);
      setIsNavigating(false);
      return;
    }
  }, [lastEvent, docListMasterId]);

  // Listen to signalR messages and update the grid data if the message is related to index details data update
  useEffect(() => {
    if (lastEvent?.payload?.type !== INDEX_DETAILS_NOTIFICATION_TYPE) return;
    const notification: IIndexDetailsDataUpdatedNotification =
      lastEvent!.payload.payload;
    if (!rows.some((row) => row.id === notification.indexDetailID)) return;

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === notification.indexDetailID
          ? {
              ...row,
              includedDocs: notification.includedDocCount,
              excludedDocs: notification.excludedDocCount,
              status: notification.status,
            }
          : row
      )
    );
    documentCountChange?.(notification.documentCount ?? 0);
  }, [lastEvent]);

  const mapIndexDetailsToRows = (
    indexDetails: IAIInvestigateIndexDetails[]
  ): ManageIndicesDetailRow[] => {
    return indexDetails.map((item, index) => ({
      id: item.indexDetailId ?? index,
      dateSubmitted: formatLastModifiedUtc(item.dateSubmitted),
      source: item.source,
      updatedBy: item.updatedBy,
      action: item.action,
      includedDocs: item.includedDocCount,
      excludedDocs: item.excludedDocCount,
      lastModifiedDate: formatLastModifiedUtc(item.lastModifiedDate),
      status: item.status,
    }));
  };

  useEffect(() => {
    if (!indexId) return;
    const payload = buildPayload();
    setLoading(true);
    getIndicesDetails(
      {indexId, payload},
      {
        onSuccess: (data: IAIInvestigateIndicesDetailsResponse) => {
          const mappedRows = mapIndexDetailsToRows(data.indexDetails);

          setRows(mappedRows);
          setRowCount(data.totalRecords);
          setLoading(false);
          documentCountChange?.(data.documentCount ?? 0);
        },
        onError: () => {
          setLoading(false);
        },
      }
    );
  }, [indexId, pageNumber, pageSize, sortModel, filterModel, triggerRefetch]);

  const columns: GridColDef[] = [
    {
      field: 'dateSubmitted',
      headerName: t(
        'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.dateSubmitted'
      ),
      flex: 1,
      minWidth: 184,
      filterable: false,
      renderCell: (params) => {
        const value = params.value ?? '';
        return (
          <CellWrapper
            toolTipContent={value}
            dataTestId={`index-detail-date-submitted-tooltip-${params.id}`}
            cellParams={params}
          >
            <Typography className="cell-content typo-text body3_size">
              {value}
            </Typography>
          </CellWrapper>
        );
      },
    },
    {
      field: 'source',
      headerName: t(
        'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.source'
      ),
      flex: 1,
      minWidth: 184,
      filterable: true,
      renderCell: (params) => {
        const value = params.value ?? '';

        return (
          <CellWrapper
            toolTipContent={value}
            dataTestId={`index-detail-source-tooltip-${params.id}`}
            cellParams={params}
          >
            <Typography className="cell-content typo-text body3_size">
              {value}
            </Typography>
          </CellWrapper>
        );
      },
      renderHeaderFilter: (params) => {
        return (
          <FilterTextField
            dataTestId="source"
            gridRenderHeaderFilterProps={params}
            placeholder={t(
              'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.filter'
            )}
            className="custom-filter"
          />
        );
      },
    },
    {
      field: 'updatedBy',
      headerName: t(
        'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.updatedBy'
      ),
      flex: 1,
      minWidth: 132,
      filterable: true,
      renderHeaderFilter: (params) => {
        return (
          <FilterTextField
            dataTestId="updatedBy"
            gridRenderHeaderFilterProps={params}
            placeholder={t(
              'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.filter'
            )}
            className="custom-filter"
          />
        );
      },
    },
    {
      field: 'action',
      headerName: t(
        'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.action'
      ),
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          className="cell-content typo-text body3_size"
          data-testid={`"manage-indices-detail-grid-action-${params.id}`}
        >
          {params.value}
        </Typography>
      ),
      renderHeaderFilter: (params) => {
        return (
          <FilterTextField
            dataTestId="action"
            gridRenderHeaderFilterProps={params}
            placeholder={t(
              'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.filter'
            )}
            className="custom-filter"
          />
        );
      },
    },
    {
      field: 'includedDocs',
      headerName: t(
        'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.includedDocs'
      ),
      type: 'number',
      minWidth: 128,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const value = params.value;

        return (
          <ArrButton
            onClick={() => {
              if (value > 0) {
                handleDocsClick(params.row, IndicesDetailDocType.Included);
              }
            }}
            variant="text"
            size="xs"
            className="id-cell-preview"
            tabIndex={params.tabIndex}
            dataTestId={`${params.id}-included-docs`}
            disabled={!value}
          >
            <Typography
              className={
                value
                  ? 'manage-indices__included cell-content typo-text body3_size'
                  : value === 0
                    ? 'cell-content typo-text body3_size manage-indices__included--zero'
                    : 'cell-content typo-text body3_size manage-indices__included--empty'
              }
              data-testid={`${params.id}-included-docs-value`}
            >
              {value?.toLocaleString() ? value.toLocaleString() : '--'}
            </Typography>
          </ArrButton>
        );
      },
      filterable: false,
    },
    {
      field: 'excludedDocs',
      headerName: t(
        'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.excludedDocs'
      ),
      type: 'number',
      minWidth: 184,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const value = params.value;

        return (
          <ArrButton
            onClick={() => {
              if (value > 0) {
                handleDocsClick(params.row, IndicesDetailDocType.Excluded);
              }
            }}
            variant="text"
            size="xs"
            className="id-cell-preview"
            tabIndex={params.tabIndex}
            dataTestId={`${params.id}-excluded-docs`}
            disabled={!value}
          >
            <Typography
              className={
                value
                  ? 'manage-indices__included cell-content typo-text body3_size'
                  : value === 0
                    ? 'cell-content typo-text body3_size manage-indices__included--zero'
                    : 'cell-content typo-text body3_size manage-indices__included--empty'
              }
              data-testid={`${params.id}-excluded-docs-value`}
            >
              {value?.toLocaleString() ? value.toLocaleString() : '--'}
            </Typography>
          </ArrButton>
        );
      },
      filterable: false,
      renderHeader: () => {
        return (
          <>
            <p
              className="excluded-header-name"
              data-testid="manage-indices-detail-grid-excluded-docs-header"
            >
              {t(
                'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.excludedDocs'
              )}
            </p>
            <ArrTooltipWrapper
              title={t(
                'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.excludedDocs'
              )}
              trigger={
                <InfoIcon
                  className="info-icon"
                  aria-hidden="true"
                  focusable="false"
                />
              }
            >
              <Trans
                i18nKey="ai.investigate.manageIndicesDetail.manageIndicesDataGrid.excludedDocsTooltipContent"
                components={{
                  br: <br data-testid="manage-indices-detail-grid-br" />,
                  strong: (
                    <strong data-testid="manage-indices-detail-grid-strong" />
                  ),
                  p: <p data-testid="manage-indices-detail-grid-p" />,
                }}
              />
            </ArrTooltipWrapper>
          </>
        );
      },
    },
    {
      field: 'lastModifiedDate',
      headerName: t(
        'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.lastModifiedDate'
      ),
      minWidth: 184,
      filterable: false,
      renderCell: (params) => {
        const value = params.value ?? '';
        return (
          <CellWrapper
            toolTipContent={value}
            dataTestId={`index-detail-modified-date-tooltip-${params.id}`}
            cellParams={params}
          >
            <Typography className="cell-content typo-text body3_size">
              {value}
            </Typography>
          </CellWrapper>
        );
      },
    },
    {
      field: 'status',
      headerName: t(
        'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.status'
      ),
      minWidth: 184,
      filterable: true,
      renderHeaderFilter: (params) => {
        return (
          <FilterTextField
            dataTestId="status"
            gridRenderHeaderFilterProps={params}
            placeholder={t(
              'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.filter'
            )}
            className="custom-filter"
          />
        );
      },
      renderCell: ({value, id, tabIndex}) => {
        const cellValue = String(value);

        const getStatusClass = () => {
          switch (cellValue) {
            case ManageIndicesStatus.Completed:
              return 'manage-indices__status--completed';
            case ManageIndicesStatus.InProgress:
              return 'manage-indices__status--progress';
            case ManageIndicesStatus.Failed:
              return 'manage-indices__status--failed';
            case ManageIndicesStatus.InQueue:
              return 'manage-indices__status--queue';
            case ManageIndicesStatus.InRetryQueue:
              return 'manage-indices__status--retry';
            case ManageIndicesStatus.Retrying:
              return 'manage-indices__status--failed';
            default:
              return '';
          }
        };

        return (
          <Box className="status-box">
            <Typography
              className={`manage-indices__status ${getStatusClass()} cell-content typo-text body3_size`}
              data-testid={`status-${id}`}
            >
              {cellValue === ManageIndicesStatus.Retrying
                ? t(
                    `ai.investigate.manageIndicesDetail.manageIndicesDataGrid.statusValue.Failed`
                  )
                : t(
                    `ai.investigate.manageIndicesDetail.manageIndicesDataGrid.statusValue.${cellValue}`,
                    cellValue
                  )}
            </Typography>
            {cellValue === ManageIndicesStatus.Failed && (
              <ArrIconButton
                size="small"
                disabled={!isActiveIndex}
                onClick={() => {
                  retryHandler(id.toString());
                }}
                icon={
                  <ResetIcon
                    fontSize="small"
                    aria-hidden="true"
                    focusable="false"
                  />
                }
                shape="none"
                ariaLabel={t(
                  'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.retryButton'
                )}
                className="retry-button"
                tabIndex={tabIndex}
                testId="manage-indices-detail-grid-retry-button-icon-button"
              />
            )}
            {cellValue === ManageIndicesStatus.Retrying && (
              <Box className="retry-loading-spinner-container">
                <ArrLoadingSpinner isOpen size={18} />
              </Box>
            )}
          </Box>
        );
      },
    },
  ];

  const mapSortFieldToApi = (field: string) => {
    switch (field) {
      case 'dateSubmitted':
        return 'DateSubmitted';
      case 'source':
        return 'Source';
      case 'updatedBy':
        return 'UpdatedBy';
      case 'action':
        return 'Action';
      case 'lastModifiedDate':
        return 'LastModifiedDate';
      case 'status':
        return 'Status';
      case 'includedDocs':
        return 'IncludedDocs';
      case 'excludedDocs':
        return 'ExcludedDocs';
      default:
        return 'LastModifiedDate';
    }
  };

  const mapFiltersToApi = (filterModel: GridFilterModel) => {
    return filterModel.items
      .filter((f) => f.value)
      .map((f) => ({
        columnName: mapSortFieldToApi(f.field),
        value: f.value,
      }));
  };
  const {mutate: getIndicesDetails} = useGetIndicesDetails();

  const buildPayload = (): IAIInvestigateIndicesDetailsPayload => ({
    sortingColumn: sortModel[0]?.field
      ? mapSortFieldToApi(sortModel[0].field)
      : 'LastModifiedDate',
    direction: sortModel[0]?.sort?.toUpperCase() || 'DESC',
    displayLength: pageSize,
    pageNumber,
    columnFilter: mapFiltersToApi(filterModel),
  });
  return (
    <Box
      className="manage-indices-grid"
      component="section"
      role="region"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <Typography
        id={titleId}
        component="h2"
        className="sr-only"
        data-testid="manage-indices-detail-grid-title"
      >
        {t('ai.investigate.manageIndicesDetail.manageIndicesDataGrid.title')}
      </Typography>
      <Typography
        id={descId}
        component="p"
        className="sr-only"
        data-testid="manage-indices-detail-grid-desc"
      >
        {t('ai.investigate.manageIndicesDetail.manageIndicesDataGrid.desc')}
      </Typography>
      {isLoading && <ArrLoadingSpinner overlay isOpen={isLoading} />}
      <ArrDataGridPro
        alwaysShowPagination={true}
        rows={rows}
        rowHeight={36}
        columns={columns}
        columnHeaderHeight={64}
        primaryKey="id"
        pagination
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        rowCount={rowCount}
        loading={loading}
        filterModel={filterModel}
        sortModel={sortModel}
        onFilterModelChange={(model) => {
          setPageNumber(1);
          setFilterModel(model);
        }}
        onSortModelChange={(model) => {
          setPageNumber(1);
          setSortModel(model);
        }}
        currentPage={pageNumber}
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        onPageChange={(p, ps) => {
          if (ps && ps !== pageSize) {
            setPageNumber(1);
            setPageSize(ps);
          } else {
            setPageNumber(p);
          }
        }}
        noRowsOverlayCustomText={t(
          'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.emptyGridMessage'
        )}
        showPerPageOptions
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disableColumnResize={false}
        headerFilters
        headerFilterHeight={48}
        disableVirtualization={false}
        gridHeight={530}
        scrollbarSize={1}
        initialState={{
          pagination: {
            paginationModel: {
              page: pageNumber - 1,
              pageSize: pageSize,
            },
          },
        }}
        disableColumnFilter
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
};

export default ManageIndicesDetailGrid;
